import { useEffect, useState, useContext } from "react";
import Error from "../../components/errors/Error";
import UploadImageForm from "./form/UploadImageForm";
import { v4 as uuidv4 } from "uuid";
import { useUpdate } from "../../contexts/UpdateContext";
import Grid from "./Grid";
import { NavigationContext } from "../../contexts/NavigationContext";
import { postImages } from "../../api/api";
import { useReport } from "../../contexts/ReportContext";
import {
  StyledHeroHeadingThin,
  StyledSpanWord,
} from "../../components/styles/Typography/Typography";
import { LoadingSpinner } from "../../components/styles/Spinners/Spinners";
import { StyledHeroContainer } from "../../components/styles/containers/Containers";

import { StyledButtonUploadImageView } from "../../components/styles/buttons/Buttons";

import {
  StyledButtonOuterUploadImageView,
  StyledButtonInner,
} from "../../components/styles/containers/Containers";

const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 20;
// Lägg till fler mimetypes vid behov
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

const UploadImageView = () => {
  const { setCurrentViewHeading, imagesToBeUploaded, setImagesToBeUploaded } =
    useUpdate();
  const [uploadingCount, setUploadingCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { dispatch } = useReport();

  useEffect(() => {
    document.title = "Felanmälan - Lägg till bilder";
    setCurrentViewHeading(
      <StyledHeroContainer>
        <StyledHeroHeadingThin>
          Lägg till <StyledSpanWord>bilder</StyledSpanWord> på{" "}
          <StyledSpanWord>ärendet</StyledSpanWord> &
          <StyledSpanWord> platsen</StyledSpanWord>
        </StyledHeroHeadingThin>
      </StyledHeroContainer>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateFiles = (files) => {
    // Kontrollera max antal
    if (imagesToBeUploaded.length + files.length > MAX_FILES) {
      return `Maximalt ${MAX_FILES} filer får laddas upp.`;
    }
    // Kontrollera varje fil
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return `Ogiltig filtyp: ${file.name}. Endast bilder och dokument är tillåtna.`;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        return `Filen ${file.name} är för stor. Maxstorlek är ${MAX_FILE_SIZE_MB} MB.`;
      }
    }
    return null;
  };

  const onImagesAdd = (images) => {
    setErrorMessage("");
    const error = validateFiles(images);
    if (error) {
      setErrorMessage(error);
      return;
    }
    images.forEach((image) => {
      processFile(image);
    });
  };

  const processFile = (file) => {
    const image = createImage(file);

    setImagesToBeUploaded((previous) => previous.concat(image));
    upload(file, image);
  };

  const onImageRemove = (id) => {
    const image = imagesToBeUploaded.find((image) => {
      return image.id === id;
    });
    const { externalId } = image;

    dispatch({
      type: "removeImage",
      payload: externalId,
    });
    setImagesToBeUploaded((previous) => {
      return previous.filter((image) => {
        return image.id !== id;
      });
    });
    //rensa eventuellaa error messages
    setErrorMessage("");
  };

  const createImage = (file) => {
    return {
      preview_URL: URL.createObjectURL(file),
      id: uuidv4(),
      uploadProgress: 0,
      fileType: file.type,
      fileName: file.name,
    };
  };

  const upload = async (file, imageMetaData) => {
    setUploadingCount((prev) => {
      return prev + 1;
    });

    try {
      const resp = await postImages(file, (progressEvent) => {
        imageMetaData.uploadProgress =
          (progressEvent.loaded * 100) / progressEvent.total;
        updateImage(imageMetaData);
      });

      setUploadingCount((prev) => {
        return prev - 1;
      });

      dispatch({
        type: "addImage",
        payload: resp.imageId,
      });

      imageMetaData.externalId = resp.imageId;
      updateImage(imageMetaData);
    } catch (error) {
      setUploadingCount((prev) => {
        return prev - 1;
      });
    }
  };

  const updateImage = (updatedImageMetaData) => {
    const index = imagesToBeUploaded.findIndex((imageMetaData) => {
      return updatedImageMetaData.id === imageMetaData.id;
    });

    setImagesToBeUploaded((previous) => {
      previous[index] = updatedImageMetaData;
      return [...previous];
    });
  };

  const buttonText = () => {
    if (uploadingCount > 0) {
      return <LoadingSpinner gray />;
    } else {
      return imagesToBeUploaded.length > 0 ? "Nästa steg" : "Hoppa över";
    }
  };

  return (
    <>
      {imagesToBeUploaded.length > 0 ? (
        <>
          <Grid
            onImagesAdd={onImagesAdd}
            onImageRemove={onImageRemove}
            images={imagesToBeUploaded}
            style={{ zIndex: 2 }}
          />
          {errorMessage && (
            <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 16px" }}>
              <Error errorMessage={errorMessage} />
            </div>
          )}
        </>
      ) : (
        <>
          <UploadImageForm
            onImagesAdd={onImagesAdd}
            errorMessage={errorMessage}
          />
        </>
      )}
      <StyledButtonOuterUploadImageView
        toggleColor={imagesToBeUploaded.length > 0}
      >
        <StyledButtonInner>
          <StyledButtonUploadImageView
            disabled={uploadingCount > 0}
            onClick={() => {
              navigationDispatch({ type: "next" });
            }}
          >
            {buttonText()}
          </StyledButtonUploadImageView>
        </StyledButtonInner>
      </StyledButtonOuterUploadImageView>
    </>
  );
};

export default UploadImageView;
